// Typed REST client for the admin SPA.
// Endpoints/contracts come from @/types (generated from OpenAPI).

import type {
  TokenPair,
  IssuedMcpToken,
  McpTokenSummary,
  OAuthConnection,
  SyncPullResponse,
  SyncPushRequest,
  SyncPushResponse,
  User,
} from "@/types";
import type { TokenProvider } from "@/auth/authSession";

/**
 * Parameters an OAuth client passes through the /authorize request, forwarded to
 * the consent endpoint to mint the authorization code.
 */
export interface OAuthConsentParams {
  clientId: string;
  redirectUri: string;
  codeChallenge: string;
  codeChallengeMethod: string;
  resource: string;
  state?: string;
}

// API Gateway's JWT authorizer denies an expired/invalid token with 403; the
// in-Lambda bearer check returns 401. Either means "re-authenticate", so we try
// a refresh on both.
const AUTH_FAILURE_STATUSES = new Set([401, 403]);

export interface ClientOptions {
  baseUrl: string;
  /** Supplies a valid access token (refreshing as needed). Omitted = anonymous. */
  tokens?: TokenProvider;
}

export class Assistant8Client {
  constructor(private readonly opts: ClientOptions) {}

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    // Proactive: ask for a token that's already fresh. Reactive (below) only
    // covers the rare case where a fresh-looking token is rejected anyway.
    const accessToken = (await this.opts.tokens?.validAccessToken()) ?? null;
    let res = await this.send(path, init, accessToken);
    if (accessToken && AUTH_FAILURE_STATUSES.has(res.status)) {
      const fresh = await this.opts.tokens?.forceRefresh();
      if (fresh) res = await this.send(path, init, fresh);
    }
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return (res.status === 204 ? undefined : await res.json()) as T;
  }

  /** One fetch with the given bearer token (if any). No refresh handling. */
  private send(path: string, init: RequestInit | undefined, token: string | null): Promise<Response> {
    return fetch(`${this.opts.baseUrl}${path}`, {
      ...init,
      headers: {
        "content-type": "application/json",
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(init?.headers ?? {}),
      },
    });
  }

  // --- Passwordless auth ---

  /** Request a magic-link email. Always resolves (no account-existence signal). */
  requestMagicLink(email: string): Promise<void> {
    return this.request<void>("/auth/magic-link/request", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  /** Exchange a magic-link token for an access + refresh token pair. */
  verifyMagicLink(token: string): Promise<TokenPair> {
    return this.request<TokenPair>("/auth/magic-link/verify", {
      method: "POST",
      body: JSON.stringify({ token }),
    });
  }

  /** Sign in with Apple: exchange an Apple identity token (+ its nonce) for tokens. */
  signInWithApple(identityToken: string, nonce: string): Promise<TokenPair> {
    return this.request<TokenPair>("/auth/apple", {
      method: "POST",
      body: JSON.stringify({ identityToken, nonce }),
    });
  }

  /** Sign out: revoke the presented refresh token's family server-side (W11). */
  logout(refreshToken: string): Promise<void> {
    return this.request<void>("/auth/logout", {
      method: "POST",
      body: JSON.stringify({ refreshToken }),
    });
  }

  /** The signed-in user's profile (id, email, devices). */
  getCurrentUser(): Promise<User> {
    return this.request<User>("/auth/me");
  }

  // --- MCP personal access tokens ---

  /** List the caller's MCP tokens as non-secret summaries (prefix + expiry). */
  listMcpTokens(): Promise<McpTokenSummary[]> {
    return this.request<McpTokenSummary[]>("/auth/mcp-tokens");
  }

  /** Mint a new MCP token. The raw `token` is returned exactly once. */
  createMcpToken(label: string): Promise<IssuedMcpToken> {
    return this.request<IssuedMcpToken>("/auth/mcp-tokens", {
      method: "POST",
      body: JSON.stringify({ label }),
    });
  }

  /** Revoke one of the caller's MCP tokens by its public id. */
  deleteMcpToken(id: string): Promise<void> {
    return this.request<void>(`/auth/mcp-tokens/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  }

  // --- OAuth (connected apps + consent) ---

  /** Approve an OAuth authorization request; returns the client redirect URL. */
  consentToAuthorize(params: OAuthConsentParams): Promise<{ redirectTo: string }> {
    return this.request<{ redirectTo: string }>("/authorize/consent", {
      method: "POST",
      body: JSON.stringify(params),
    });
  }

  /**
   * Decline an OAuth authorization request. The backend re-validates the
   * redirect_uri against the registered client before returning the redirect URL,
   * so the browser is never sent to an unvalidated `redirect_uri` (audit M6).
   */
  denyAuthorize(params: {
    clientId: string;
    redirectUri: string;
    state?: string;
  }): Promise<{ redirectTo: string }> {
    return this.request<{ redirectTo: string }>("/authorize/deny", {
      method: "POST",
      body: JSON.stringify(params),
    });
  }

  /** List the caller's connected OAuth apps. */
  listOAuthConnections(): Promise<OAuthConnection[]> {
    return this.request<OAuthConnection[]>("/oauth/connections");
  }

  /** Revoke a connected OAuth app by grant id. */
  revokeOAuthConnection(id: string): Promise<void> {
    return this.request<void>(`/oauth/connections/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  }

  // --- Sync (docs/architecture/sync-system.md §5) ---

  /** One page of the change feed after the cursor (state-based, compacted). */
  pullSync(since: string, limit?: number): Promise<SyncPullResponse> {
    const params = new URLSearchParams({ since });
    if (limit !== undefined) params.set("limit", String(limit));
    return this.request<SyncPullResponse>(`/sync?${params}`);
  }

  /** Apply a batch of field-level ops with server-side merge. */
  pushSync(request: SyncPushRequest): Promise<SyncPushResponse> {
    return this.request<SyncPushResponse>("/sync", {
      method: "POST",
      body: JSON.stringify(request),
    });
  }
}
